<template>
  <div class="audio-player">
    <button
      class="control-btn"
      @click="handlePlay"
      :disabled="!isReady"
      :class="{ 'is-playing': isPlaying }"
    >
      <span class="icon">🔉 {{ playButtonText }}</span>
      <span class="btn-text">{{ buttonLabel }}</span>
      <span v-if="duration" class="duration">{{formatTime(duration)}}</span>
    </button>

    <div class="loading-indicator" v-if="loading">
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
    </div>
  </div>
</template>

<script>
import { AudioService } from "../Services/audioService";

export default {
  name: "audio-player",

  data() {
    return {
      duration: 0,
      playback: null,
      loading: false,
      isPlaying: false,
      isReady: false
    }
  },

  computed: {
    playButtonText() {
      return this.isPlaying ? '⏹️' : '▶️';
    },
    buttonLabel() {
      if (!this.isReady) return 'Loading...';
      return this.isPlaying ? 'Stop' : 'Play';
    }
  },

  props: {
    src: {
      type: String,
      default: "",
    }
  },

  async mounted() {
    this.playback = new AudioService();
    this.playback.addEventListener('playing', this.handlePlaying);
    this.playback.addEventListener('stopped', this.handleStopped);
    this.loading = true;

    try {
      const {duration} = await this.playback.init(this.src);
      this.duration = duration;
      this.isReady = true;
    } catch (error) {
      console.error('Failed to load audio:', error);
    } finally {
      this.loading = false;
    }
  },

  beforeDestroy() {
    if (this.playback) {
      this.playback.stop();
      this.playback.removeEventListener('playing', this.handlePlaying);
      this.playback.removeEventListener('stopped', this.handleStopped);
    }
  },

  methods: {
    formatTime(duration) {
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      const seconds = Math.floor(duration % 60);

      return `${hours ? `${hours}:` : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    },
    handleStopped() {
      this.isPlaying = false;
    },
    handlePlaying() {
      this.isPlaying = true;
    },
    async handlePlay() {
      if (!this.isReady) return;

      if (this.isPlaying) {
        this.playback.stop();
        this.isPlaying = false;
      } else {
        try {
          await this.playback.play(this.src);
          this.isPlaying = true;
        } catch (error) {
          console.error('Failed to play audio:', error);
        } finally {
          this.loading = false;
        }
      }
    }
  }
}
</script>

<style>
.audio-player {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
}

.control-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f0f0f0;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}
.control-btn:active,
.control-btn:focus,
.control-btn:hover {
  outline: none;
}

.control-btn:hover:not(:disabled) {
  background: #e0e0e0;
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.control-btn.is-playing {
  background: #e8f0fe;
  color: #1a73e8;
}

.icon {
  font-size: 1.2rem;
}

.btn-text {
  font-weight: 500;
}

.loading-indicator {
  display: flex;
  gap: 0.3rem;
  align-items: center;
}

.dot {
  width: 6px;
  height: 6px;
  background: #666;
  border-radius: 50%;
  animation: bounce 0.5s infinite alternate;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-4px);
  }
}
</style>
